import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreDocument, QueryFn } from '@angular/fire/firestore';
import { of, combineLatest, Subject, BehaviorSubject, Observable } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import firebase from 'firebase/app';

import { getFirebaseBackend } from '../../authUtils';

import { User } from '../models/auth.models';
import { MyUser} from '../models/myUser.models';
import { flatMap } from 'rxjs/internal/operators';
import { Partner } from 'src/app/core/models/partner.models';
import { environment } from 'src/environments/environment';
import { AngularFireFunctions } from '@angular/fire/functions';

@Injectable({ providedIn: 'root' })

export class AuthenticationService {

    user: User;
    partnerValidationData$ = new BehaviorSubject<any>(null);

    constructor(
      private fs: AngularFirestore,
      private fireFunctions: AngularFireFunctions) {}

    /**
     * Returns the current user
     */
    public currentUser(): User {
        return getFirebaseBackend().getAuthenticatedUser();
    }

    /**
     * Performs the auth
     * @param email email of user
     * @param password password of user
     */
    login(email: string, password: string) {
        return getFirebaseBackend().loginUser(email, password).then((response: any) => {
            const user = response;
            return user;
        });
    }

    /**
     * Performs the register
     * @param email email
     * @param password password
     */
    register(email: string, password: string) {
        return getFirebaseBackend().registerUser(email, password).then((response: any) => {
            const user = response;
            return user;
        });
    }

    /**
     * Reset password
     * @param email email
     */
    resetPassword(email: string) {
        return getFirebaseBackend().forgetPassword(email).then((response: any) => {
            const message = response.data;
            return message;
        });
    }

    /**
     * Logout the user
     */
    logout() {
        // logout the user
        getFirebaseBackend().logout();
    }

    getUserRole(hostId: string) {
      const cu = this.currentUser();
      if (cu) {
        return this.fs.doc('roles/sysAdmin').get().pipe(
          map(doc => {
            return doc.data();
          }),
          map((sysAdmins: {users: string[]}) => {
            return sysAdmins?.users.findIndex(id => id === cu.uid) > -1
          }),
          switchMap(sysAdmin => {
            if (sysAdmin) {
              return of('sysAdmin');
            } else if (hostId){
              return this.fs.doc(`hosts/${hostId}/admins/hostAdmins`).get().pipe(
                map(doc => {
                  return doc.data();
                }),
                map((hostAdmins: {ids: string[]}) => {
                  return hostAdmins?.ids.findIndex(id => id === cu.uid) > -1
                }),
                switchMap(hostAdmin => {
                  if (hostAdmin) {
                    return of('hostAdmin');
                  } else {
                    return this.fs.doc(`hosts/${hostId}/admins/users`).get().pipe(
                      map(doc => {
                        return doc.data();
                      }),
                      map((users: {ids: string[]}) => {
                        return users?.ids.findIndex(id => id === cu.uid) > -1
                      }),
                      map(isUser => isUser ? 'user' : '')
                    );
                  }
                })
              )
            } else {
              return of('');
            }
          })
        );
      } else {
        return of('');
      }
    }

    getUser(uid: string) {
      // let qFn: QueryFn<firebase.firestore.DocumentData> = null;
      // if (role) {
      //   qFn = p => p.where('parentProgramId', '==', parentProgramId);
      // }
      return uid ? this.fs.collection<MyUser>('users').doc(uid).valueChanges() : null;
    }

    setUserAvatar(uid: string, url: string) {

      const fbUser = getFirebaseBackend().getCurrentFbUser(uid) //, photoUrl: url};
      return fbUser.updateProfile({
        photoURL: url
      }).then(() => {
        return this.fs.collection('users').doc(uid).update({photoURL: url});
      }).catch((error) => {
        // An error occurred
        // ...
      });
    }

    setUserDisplayName(uid: string, name) {
      const fbUser = getFirebaseBackend().getCurrentFbUser(uid) //, photoUrl: url};
      return fbUser.updateProfile({
        displayName: name,
      }).then(() => {
        // sessionStorage.setItem('authUser', JSON.stringify({...fbUser, displayName: name}));
        return this.fs.collection('users').doc(uid).update({displayName: name});
      }).catch((error) => {
        // An error occurred
        // ...
      });

    }

    getUserByEmail(email: string) {
      // let qFn: QueryFn<firebase.firestore.DocumentData> = null;
      // if (role) {
      //   qFn = p => p.where('parentProgramId', '==', parentProgramId);
      // }
      return email ? this.fs.collection<MyUser>('users', q => q.where('email', '==', email)).valueChanges() : null;
    }

    getHostUsers(hostId: string, roleId: string) {
      // return this.fs.doc(`hosts/${hostId}/admins/${roleId}`).get().pipe(
      //   map(doc => {
      //     return doc.exists ? doc.data() : null;
      //   }),
      return this.fs.doc(`hosts/${hostId}/admins/${roleId}`).valueChanges().pipe(
        map(data => {
          return data ? data : null;
        }),
        map((adminDocData: {ids: string[]}) => {
          if (adminDocData?.ids?.length) {
            return adminDocData.ids.map(id => this.getUser(id))
          } else {
            return [];
          }
        }),
        flatMap(ids => {
          // console.log(ids);
          return ids?.length ? combineLatest(ids) : of(null);
        })
      )
    }

    addUserToHost(uid: string, hostId: string, rolePath: 'users' | 'hostAdmins') {
      const docref = this.fs.doc(`hosts/${hostId}/admins/${rolePath}`);
      return docref.get().toPromise().then(df => {
        if (df.exists) {
          return docref.update({
            ids: firebase.firestore.FieldValue.arrayUnion(...[uid])
          });
        } else {
          return docref.set({
            ids: firebase.firestore.FieldValue.arrayUnion(...[uid])
          });
        }
      });
    }

    validatePartnerByPrivateKey(privateKey: string, useEmulator?: boolean) {
      if (useEmulator && !environment.production) {
        this.fireFunctions.useEmulator('localhost', 5001);
      }

      const validatePartner = this.fireFunctions.httpsCallable('partnerModule');
      return validatePartner({
        mode: 'validate-partner',
        keyId: privateKey,
        codeType: 'private',
        }).pipe(
        first(),
        map(res => {
          // this.logger.createTimeLog(this.host.id, this.pack.id, this.logIdReady$.value, '8openDoorAdminRespond', req.id);
          if (res.error) {
            throw new Error(res.error);
          }
          return res?.data;
        }),
      ).toPromise().then((r) => {
        this.partnerValidationData$.next(r);
        return Promise.resolve(r);
      }).catch((e) => {
        this.partnerValidationData$.next(null);
        return Promise.reject(e);
      });
    }

    resetPartner() {
      this.partnerValidationData$.next(null);
    }
}

